<section class="flex flex-col flex-auto min-w-0 w-full bg-slate-50">
  <div class="max-w-5xl w-full mx-auto px-6 pt-8 pb-10">
    <div
      class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden"
    >
      <div
        class="px-6 py-4 border-b border-slate-100 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
      >
        <div class="flex items-center gap-3">
          <span
            class="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-sky-100 flex-shrink-0"
          >
            <mat-icon class="!text-sky-700 !text-lg !leading-none"
              >table_view</mat-icon
            >
          </span>
          <div class="min-w-0">
            <div class="flex items-center gap-2">
              <h3 class="text-base font-semibold text-slate-800">
                Loan Pipeline Report
              </h3>
              <span
                class="flex-shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 tracking-wide"
                >CSV</span
              >
            </div>
            <p class="text-sm text-slate-500 mt-0.5">
              Dynamic application export
            </p>
          </div>
        </div>

        <mat-form-field appearance="outline" class="w-full sm:w-72">
          <mat-label>Product Category</mat-label>
          <select
            matNativeControl
            [value]="selectedProductCategoryCode()"
            [disabled]="productCategoriesLoading() || downloading()"
            (change)="onProductCategoryChange($event)"
          >
            @for (
              productCategory of productCategories();
              track productCategory.code
            ) {
              <option [value]="productCategory.code">
                {{ productCategory.name }}
              </option>
            }
          </select>
        </mat-form-field>
      </div>

      <div class="px-6 pt-5 pb-5 bg-slate-50/60">
        <form
          [formGroup]="filterForm"
          (ngSubmit)="download()"
          class="flex flex-col gap-4"
        >
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4">
            <mat-form-field appearance="outline" class="w-full">
              <mat-label>Branch</mat-label>
              <select
                matNativeControl
                formControlName="branchSetCode"
                [disabled]="branchesLoading() || downloading()"
              >
                <option value="">
                  {{
                    branchesLoading() ? "Loading branches..." : "All branches"
                  }}
                </option>
                @for (branch of branches(); track branch.code) {
                  <option [value]="branch.code">
                    {{ branchOptionLabel(branch) }}
                  </option>
                }
              </select>
            </mat-form-field>

            <mat-form-field appearance="outline" class="w-full">
              <mat-label>Stage</mat-label>
              <select
                matNativeControl
                formControlName="stage"
                [disabled]="
                  stagesLoading() ||
                  downloading() ||
                  !selectedProductCategoryCode()
                "
              >
                <option value="">
                  {{ stagesLoading() ? "Loading stages..." : "All stages" }}
                </option>
                @for (stage of stages(); track stage.id) {
                  <option [value]="stage.stage">
                    {{ stage.stage }}
                  </option>
                }
              </select>
            </mat-form-field>

            <mat-form-field appearance="outline" class="w-full">
              <mat-label>Created From</mat-label>
              <input
                matInput
                [matDatepicker]="createdFromPicker"
                [max]="today"
                formControlName="createdDateFrom"
                placeholder="Choose a date"
              />
              <mat-datepicker-toggle
                matSuffix
                [for]="createdFromPicker"
              ></mat-datepicker-toggle>
              <mat-datepicker #createdFromPicker></mat-datepicker>
            </mat-form-field>

            <mat-form-field appearance="outline" class="w-full">
              <mat-label>Created To</mat-label>
              <input
                matInput
                [matDatepicker]="createdToPicker"
                [max]="today"
                formControlName="createdDateTo"
                placeholder="Choose a date"
              />
              <mat-datepicker-toggle
                matSuffix
                [for]="createdToPicker"
              ></mat-datepicker-toggle>
              <mat-datepicker #createdToPicker></mat-datepicker>
            </mat-form-field>
          </div>

          <div
            class="flex flex-col gap-2 pt-3 border-t border-slate-100 sm:flex-row sm:items-center sm:justify-end"
          >
            <button
              mat-stroked-button
              color="warn"
              type="button"
              (click)="clearFilters()"
              [disabled]="downloading()"
              class="sm:order-1"
            >
              <mat-icon class="!h-4 !w-4 !text-[16px]">clear_all</mat-icon>
              Clear
            </button>
            <button
              mat-raised-button
              color="primary"
              type="submit"
              [disabled]="downloading() || !selectedProductCategoryCode()"
              class="sm:order-2"
            >
              <mat-icon class="!h-4 !w-4 !text-[16px]">download</mat-icon>
              {{ downloading() ? "Preparing" : "Download CSV" }}
            </button>
          </div>
        </form>

        @if (filterLoadMessage()) {
          <div
            class="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2"
          >
            <mat-icon
              class="!text-amber-600 !text-lg !w-5 !h-5 !leading-none flex-shrink-0"
              >info</mat-icon
            >
            <p class="text-xs text-amber-700">
              {{ filterLoadMessage() }}
            </p>
          </div>
        }

        @if (filledFilters().length > 0) {
          <mat-chip-set
            aria-label="Selected search filters"
            class="flex flex-wrap gap-2 mt-4"
          >
            @for (filter of filledFilters(); track filter.label) {
              <mat-chip class="rounded-full bg-sky-100 text-sky-700">
                {{ filter.label }}: {{ filter.value }}
              </mat-chip>
            }
          </mat-chip-set>
        }

        @if (status() === "done") {
          <div
            class="mt-3 flex items-start gap-2 rounded-lg bg-emerald-50 border border-emerald-200 px-3 py-2"
          >
            <mat-icon
              class="!text-emerald-600 !text-lg !w-5 !h-5 !leading-none flex-shrink-0"
              >check_circle</mat-icon
            >
            <p class="text-xs text-emerald-700">
              CSV export ready - check your downloads.
            </p>
          </div>
        }

        @if (errorMessage()) {
          <div
            class="mt-3 flex items-start gap-2 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2"
          >
            <mat-icon
              class="!text-rose-600 !text-lg !w-5 !h-5 !leading-none flex-shrink-0"
              >error_outline</mat-icon
            >
            <p class="text-xs text-rose-700">{{ errorMessage() }}</p>
          </div>
        }
      </div>
    </div>
  </div>
</section>
